import { useState, useEffect } from 'react';
import { formatDriverId, formatTruckId } from '../../utils/idUtils';
import { 
  ChevronLeft, 
  MapPin, 
  Calendar, 
  Clock, 
  Truck, 
  User, 
  Package,
  FileText,
  RefreshCw,
  Edit,
  Play,
  History,
  MessageSquare,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Grid3x3,
  List,
  Loader2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { downloadDocument } from '../../utils/downloadDocument';
import { useToast } from '../../contexts/ToastContext';
import { getDispatchSMSLogs, sendDispatchSMS } from '../../services/smsService';

interface DispatchDetail {
  id: string;
  dispatchNumber: string;
  loadId: string;
  loadNumber: string;
  driverId: string;
  driverName: string;
  driverPhone: string;
  driverType?: string;
  truckId: string;
  truckNumber: string;
  pickupLocation: string;
  pickupCity: string;
  pickupState: string;
  pickupTime: string;
  deliveryLocation: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryTime: string;
  rescheduledDeliveryDate?: string;
  rescheduledDeliveryTimeFrom?: string;
  rescheduledDeliveryTimeTo?: string;
  distance: number;
  rate: number;
  driverPay: number;
  emptyMiles: number;
  status: string;
  assignedAt: string;
  assignedBy: string;
  notes?: string;
  specialInstructions?: string;
  commodity?: string;
  weight?: number;
  allStops?: any[];
}

interface DispatchDetailViewProps {
  dispatchId: string;
  onBack: () => void;
}

// Entries of `documents`: load documents keep the storage path in `url`,
// SMS attachments keep an absolute URL there.
interface DocumentEntry {
  source?: string;
  name?: string;
  filename?: string;
  url?: string;
  file_path?: string;
  messageId?: string;
}

// Document Preview Component with PDF thumbnail support
function DocumentPreview({ doc, onImageClick, onPdfClick }: { doc: any; onImageClick: () => void; onPdfClick?: (url: string) => void }) {
  const [pdfThumbnail, setPdfThumbnail] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  
  useEffect(() => {
    const getSignedUrl = async () => {
      // Get signed URL from Supabase storage
      const filePath = doc.url || doc.file_path;
      if (filePath) {
        const { data } = await supabase.storage
          .from('company-documents')
          .createSignedUrl(filePath, 3600);
        
        if (data?.signedUrl) {
          setFileUrl(data.signedUrl);
          
          // Generate PDF thumbnail if it's a PDF
          if (doc.type === 'pdf' || filePath?.toLowerCase().endsWith('.pdf')) {
            generatePdfThumbnail(data.signedUrl);
          }
        }
      }
    };
    getSignedUrl();
  }, [doc.url, doc.file_path]);
  
  const generatePdfThumbnail = async (url: string) => {
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/build/pdf.worker.min.mjs`;
      
      const loadingTask = pdfjsLib.getDocument({
        url: url,
        cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.449/cmaps/',
        cMapPacked: true,
      });
      
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      
      const scale = 0.5;
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      
      if (context) {
        await page.render({ canvasContext: context, viewport: viewport } as any).promise;
        const thumbnailData = canvas.toDataURL('image/jpeg', 0.8);
        setPdfThumbnail(thumbnailData);
      }
    } catch (error) {
      console.error('[PDF Thumbnail] Error:', error);
    }
  };
  
  const isImage = doc.type === 'image' || doc.contentType?.startsWith('image/');
  const isPDF = doc.type === 'pdf' || doc.url?.toLowerCase().endsWith('.pdf');
  
  if (isImage) {
    return (
      <button onClick={onImageClick} className="w-full cursor-pointer">
        <img 
          src={doc.url} 
          alt={doc.filename || 'Image'}
          className="w-full h-40 object-cover rounded-lg border border-slate-300 hover:opacity-90 transition-opacity"
        />
      </button>
    );
  }
  
  if (isPDF) {
    return (
      <button 
        onClick={() => onPdfClick?.(fileUrl)}
        className="relative w-full h-40 bg-red-50 rounded-lg flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
      >
        {pdfThumbnail ? (
          <img 
            src={pdfThumbnail} 
            alt={doc.name || 'PDF'}
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <FileText className="w-16 h-16 text-red-600" />
        )}
        <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded font-semibold">
          PDF
        </div>
      </button>
    );
  }
  
  return (
    <div className="w-full h-40 bg-slate-100 rounded-lg flex items-center justify-center">
      <FileText className="w-16 h-16 text-slate-400" />
    </div>
  );
}

export function DispatchDetailView({ dispatchId, onBack }: DispatchDetailViewProps) {
  const toast = useToast();
  const [dispatch, setDispatch] = useState<DispatchDetail | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'documents'>('summary');
  const [loading, setLoading] = useState(true);
  const [smsLogs, setSmsLogs] = useState<any[]>([]);
  const [smsStatus, setSmsStatus] = useState<{sent: boolean; status: string; error?: string} | null>(null);
  const [resendingSMS, setResendingSMS] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewPdf, setPreviewPdf] = useState<string | null>(null);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsViewMode, setDocumentsViewMode] = useState<'grid' | 'list'>('grid');
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; label: string } | null>(null);
  const [downloadingDocKey, setDownloadingDocKey] = useState<string | null>(null);

  // Same name the cards show: load documents keep their own name, SMS
  // attachments get Driver_Dispatch[_n].ext.
  const getDocumentFileName = (doc: DocumentEntry, idx: number) => {
    if (doc.source === 'load_documents') return doc.name || 'Document';
    const extension = doc.filename ? doc.filename.split('.').pop() : 'file';
    const driverName = dispatch?.driverName?.replace(/\s+/g, '_') || 'Driver';
    const docNum = idx + 1;
    return docNum === 1
      ? `${driverName}_${dispatch?.dispatchNumber}.${extension}`
      : `${driverName}_${dispatch?.dispatchNumber}_${docNum}.${extension}`;
  };

  // doc.url is a storage path for load documents and an absolute URL for SMS
  // attachments; downloadDocument handles both. This used to be an <a download>,
  // which opened a tab for SMS attachments and pointed at a non-URL path for
  // load documents.
  const handleDownloadDocument = async (doc: DocumentEntry, idx: number) => {
    const key = `${doc.messageId}-${idx}`;
    setDownloadingDocKey(key);
    try {
      await downloadDocument(doc.url || doc.file_path || '', getDocumentFileName(doc, idx));
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloadingDocKey(null);
    }
  };

  useEffect(() => {
    loadDispatchDetails();
    loadSMSData();
    loadDocuments();

    // Set up real-time subscription for SMS messages
    const channel = supabase
      .channel(`dispatch-sms-${dispatchId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sms_messages',
        },
        (payload) => {
          console.log('SMS message change detected:', payload);
          loadSMSData(); // Reload SMS data when messages change
          loadDocuments(); // Reload documents to sync attachments
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'dispatches',
          filter: `id=eq.${dispatchId}`,
        },
        (payload) => {
          console.log('Dispatch updated:', payload);
          loadSMSData(); // Reload when dispatch is updated (e.g., thread_id added)
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dispatchId]);

  async function loadDispatchDetails() {
    try {
      const { data, error } = await supabase
        .from('dispatches')
        .select(`
          id,
          dispatch_number,
          load_id,
          driver_id,
          truck_id,
          status,
          empty_miles,
          driver_pay,
          created_by,
          created_at,
          loads!dispatches_load_id_fkey (
            load_number,
            pickup_city,
            pickup_state,
            delivery_city,
            delivery_state,
            pickup_date,
            delivery_date,
            rescheduled_delivery_date,
            rescheduled_delivery_time_from,
            rescheduled_delivery_time_to,
            commodity,
            weight,
            total_miles,
            total_rate,
            special_instructions,
            load_stops!load_stops_load_id_fkey(
              stop_type,
              stop_order,
              facility_name,
              street,
              city,
              state,
              zip_code,
              date,
              time_from,
              time_to,
              contact_person,
              phone_number,
              weight,
              pieces,
              instructions
            )
          ),
          drivers!dispatches_driver_id_fkey (
            first_name,
            last_name,
            phone_number,
            driver_type
          ),
          trucks!dispatches_truck_id_fkey (
            unit_number
          )
        `)
        .eq('id', dispatchId)
        .single();

      if (error) throw error;

      console.log('📋 [DispatchDetailView] Raw dispatch data:', data);
      console.log('📋 [DispatchDetailView] load_id from data:', data?.load_id);

      if (data) {
        // Fetch user info separately if created_by exists
        let createdByName = 'System';
        if (data.created_by) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', data.created_by)
            .single();
          
          if (profileData) {
            createdByName = profileData.full_name || 'System';
          }
        }

        // Map to DispatchDetail format
        // Handle foreign key relations (Supabase returns them as arrays, take first element)
        const load = Array.isArray(data.loads) ? data.loads[0] : data.loads;
        const driver = Array.isArray(data.drivers) ? data.drivers[0] : data.drivers;
        const truck = Array.isArray(data.trucks) ? data.trucks[0] : data.trucks;
        
        // Process load_stops if available
        let pickupCity = load?.pickup_city || '';
        let pickupState = load?.pickup_state || '';
        let pickupTime = load?.pickup_date || '';
        let deliveryCity = load?.delivery_city || '';
        let deliveryState = load?.delivery_state || '';
        let deliveryTime = load?.delivery_date || '';
        let allStops: any[] = [];

        if (load?.load_stops && load.load_stops.length > 0) {
          // Sort stops by stop_order
          const sortedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
          allStops = sortedStops;
          
          // Get first pickup stop
          const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper');
          if (pickupStop) {
            pickupCity = pickupStop.city;
            pickupState = pickupStop.state;
            pickupTime = pickupStop.date ? 
              (pickupStop.time_from ? `${pickupStop.date}T${pickupStop.time_from}` : pickupStop.date) : '';
          }
          
          // Get last delivery stop
          const deliveryStops = sortedStops.filter((s: any) => s.stop_type === 'consignee');
          if (deliveryStops.length > 0) {
            const lastDelivery = deliveryStops[deliveryStops.length - 1];
            deliveryCity = lastDelivery.city;
            deliveryState = lastDelivery.state;
            deliveryTime = lastDelivery.date ? 
              (lastDelivery.time_from ? `${lastDelivery.date}T${lastDelivery.time_from}` : lastDelivery.date) : '';
          }
        }
        
        const mapped = {
          id: data.id,
          dispatchNumber: data.dispatch_number,
          loadId: data.load_id,
          loadNumber: load?.load_number || 'N/A',
          driverId: data.driver_id,
          driverName: driver ? `${driver.first_name} ${driver.last_name}` : 'N/A',
          driverPhone: driver?.phone_number || '',
          driverType: driver?.driver_type || '',
          truckId: data.truck_id,
          truckNumber: truck?.unit_number || 'N/A',
          pickupLocation: pickupCity && pickupState ? `${pickupCity}, ${pickupState}` : '',
          pickupCity: pickupCity,
          pickupState: pickupState,
          pickupTime: pickupTime,
          deliveryLocation: deliveryCity && deliveryState ? `${deliveryCity}, ${deliveryState}` : '',
          deliveryCity: deliveryCity,
          deliveryState: deliveryState,
          deliveryTime: deliveryTime,
          allStops: allStops,
          rescheduledDeliveryDate: load?.rescheduled_delivery_date || '',
          rescheduledDeliveryTimeFrom: load?.rescheduled_delivery_time_from || '',
          rescheduledDeliveryTimeTo: load?.rescheduled_delivery_time_to || '',
          distance: load?.total_miles || 0,
          commodity: load?.commodity || '',
          weight: load?.weight || 0,
          rate: load?.total_rate || 0,
          driverPay: data.driver_pay || 0,
          emptyMiles: data.empty_miles || 0,
          status: data.status,
          assignedBy: createdByName,
          assignedAt: data.created_at,
          specialInstructions: load?.special_instructions || '',
        };
        
        console.log('📋 [DispatchDetailView] Mapped dispatch object:', mapped);
        console.log('📋 [DispatchDetailView] Mapped loadId:', mapped.loadId);
        
        setDispatch(mapped);
        
        // Load documents immediately with the loadId we just fetched
        if (mapped.loadId) {
          loadDocuments(mapped.loadId);
        }
      }
    } catch (error) {
      console.error('Error loading dispatch:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleResendSMS = async () => {
    if (!dispatch) return;

    setResendingSMS(true);
    try {
      const smsResult = await sendDispatchSMS({
        dispatchId: dispatchId,
        loadId: dispatch.loadId,
        driverId: dispatch.driverId,
        truckId: dispatch.truckId,
      });

      if (smsResult.success) {
        toast.success('SMS resent successfully!');
      } else {
        toast.error(`Failed to resend SMS: ${smsResult.error}`);
      }

      // Reload SMS data
      loadSMSData();
    } catch (error) {
      console.error('Error resending SMS:', error);
      toast.error('Failed to resend SMS');
    } finally {
      setResendingSMS(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !dispatch) return;

    setSendingMessage(true);
    try {
      const { sendThreadReply } = await import('../../services/smsThreading.service');
      
      // Get the dispatch's thread ID
      const { data: dispatchData } = await supabase
        .from('dispatches')
        .select('sms_thread_id')
        .eq('id', dispatchId)
        .single();

      if (!dispatchData?.sms_thread_id) {
        toast.error('No SMS thread found for this dispatch');
        return;
      }

      // Send message through threading system
      const success = await sendThreadReply(
        dispatchData.sms_thread_id,
        dispatch.driverId,
        dispatch.driverPhone || '',
        newMessage
      );

      if (success) {
        toast.success('Message sent!');
        setNewMessage('');
        loadSMSData(); // Reload messages
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  async function loadSMSData() {
    try {
      // Load SMS status from dispatches table
      const { data: dispatchData, error: dispatchError } = await supabase
        .from('dispatches')
        .select('sms_sent, sms_status, sms_sent_at, sms_error, sms_thread_id')
        .eq('id', dispatchId)
        .single();

      if (!dispatchError && dispatchData) {
        setSmsStatus({
          sent: dispatchData.sms_sent,
          status: dispatchData.sms_status,
          error: dispatchData.sms_error
        });

        console.log('Dispatch SMS status:', {
          sent: dispatchData.sms_sent,
          status: dispatchData.sms_status,
          threadId: dispatchData.sms_thread_id
        });
      }

      // Load SMS logs
      const logs = await getDispatchSMSLogs(dispatchId);
      console.log('Loaded SMS logs:', logs.length, 'messages');
      setSmsLogs(logs);
    } catch (error) {
      console.error('Error loading SMS data:', error);
    }
  }

  async function loadDocuments(loadId?: string) {
    try {
      // Use provided loadId or fall back to dispatch state
      const actualLoadId = loadId || dispatch?.loadId;
      
      console.log('📄 [DispatchDetailView] Loading documents for dispatch:', dispatchId);
      console.log('📄 [DispatchDetailView] Using loadId:', actualLoadId);
      
      const allDocs: any[] = [];

      // 1. Load documents from load_documents table (rate confirmations, BOLs, PODs, etc.)
      if (actualLoadId) {
        console.log('📄 [DispatchDetailView] Querying load_documents for load_id:', actualLoadId);
        
        const { data: loadDocs, error: loadDocsError } = await supabase
          .from('load_documents')
          .select(`
            id,
            document_type,
            created_at,
            documents (
              id,
              name,
              file_path,
              file_type,
              file_size,
              created_at
            )
          `)
          .eq('load_id', actualLoadId);

        console.log('📄 [DispatchDetailView] Load documents query result:', loadDocs);
        console.log('📄 [DispatchDetailView] Load documents error:', loadDocsError);

        if (!loadDocsError && loadDocs) {
          loadDocs.forEach((ld: any) => {
            if (ld.documents) {
              allDocs.push({
                id: ld.documents.id,
                name: ld.documents.name,
                url: ld.documents.file_path,
                type: ld.documents.file_type,
                size: ld.documents.file_size,
                document_type: ld.document_type,
                timestamp: ld.created_at,
                source: 'load_documents'
              });
            }
          });
          console.log('📄 [DispatchDetailView] Added', allDocs.length, 'documents from load_documents');
        }
      } else {
        console.log('⚠️ [DispatchDetailView] No loadId found on dispatch object');
      }

      // 2. Load SMS attachments
      const { data: dispatchData } = await supabase
        .from('dispatches')
        .select('sms_thread_id')
        .eq('id', dispatchId)
        .single();

      if (dispatchData?.sms_thread_id) {
        // Load all messages from this thread that have attachments
        const { data: messages, error } = await supabase
          .from('sms_messages')
          .select('id, content, attachments, direction, created_at, sent_at, received_at')
          .eq('thread_id', dispatchData.sms_thread_id)
          .not('attachments', 'is', null)
          .order('created_at', { ascending: false });

        if (!error && messages) {
          // Flatten all attachments from all messages
          messages.forEach((msg) => {
            if (msg.attachments && Array.isArray(msg.attachments)) {
              msg.attachments.forEach((att: any) => {
                allDocs.push({
                  ...att,
                  messageId: msg.id,
                  messageContent: msg.content,
                  direction: msg.direction,
                  timestamp: msg.sent_at || msg.received_at || msg.created_at,
                  source: 'sms'
                });
              });
            }
          });
        }
      }

      // Sort all documents by timestamp (newest first)
      allDocs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setDocuments(allDocs);
      
      // Auto-sync SMS documents to Documents page (with duplicate prevention)
      const smsDocuments = allDocs.filter(doc => doc.source === 'sms');
      if (smsDocuments.length > 0 && dispatch) {
        await syncDocumentsToDocumentsPage(smsDocuments);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  }

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (files.length === 0 || !dispatch) return;

    await processFileBatch(files);

    // Reset file input
    input.value = '';
  }

  // Files are uploaded one at a time on purpose: saveExtraUploadToDriverFolder
  // creates the document_folders rows on demand and resolves name collisions by
  // listing the folder, so parallel calls would race on both.
  async function processFileBatch(files: File[]) {
    if (!dispatch || files.length === 0) return;

    setUploadProgress({ current: 0, total: files.length, label: '' });

    try {
      // Import the saveExtraUploadToDriverFolder utility
      const { saveExtraUploadToDriverFolder } = await import('../../utils/rateConfirmationStorage');

      // The driver folder and pickup date are the same for every file of the
      // batch, so they are resolved once here instead of once per file
      const driverName = dispatch.driverName;
      const pickupDate = new Date(dispatch.pickupTime);

      // A file that fails must not abort the rest of the batch
      const failed: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress({ current: i + 1, total: files.length, label: file.name });
        try {
          await processFileUpload(file, driverName, pickupDate, saveExtraUploadToDriverFolder);
        } catch (error) {
          console.error('Error uploading file:', file.name, error);
          failed.push(`${file.name} (${(error as { message?: string })?.message || 'upload failed'})`);
        }
      }

      const uploaded = files.length - failed.length;
      if (uploaded > 0) {
        toast.success(uploaded === 1 ? 'File uploaded successfully!' : `${uploaded} files uploaded successfully!`);
      }
      if (failed.length > 0) {
        toast.error(`Failed to upload ${failed.length} of ${files.length}: ${failed.join(', ')}`);
      }

      // Reload documents once for the whole batch
      if (dispatch.loadId) {
        loadDocuments(dispatch.loadId);
      }
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast.error(error.message || 'Failed to upload file');
    } finally {
      setUploadProgress(null);
    }
  }

  async function processFileUpload(
    file: File,
    driverName: string,
    pickupDate: Date,
    saveExtraUploadToDriverFolder: typeof import('../../utils/rateConfirmationStorage')['saveExtraUploadToDriverFolder']
  ) {
    if (!dispatch) return;

    // Generate unique filename and sanitize it (remove spaces and special characters)
    const timestamp = Date.now();
    const sanitizedFileName = file.name
      .replace(/\s+/g, '_')  // Replace spaces with underscores
      .replace(/[^\w\-_.]/g, '');  // Remove special characters except dash, underscore, dot
    // Random suffix so two same-named files in one batch can't collide here
    const fileName = `${timestamp}_${Math.random().toString(36).substring(2, 8)}_${sanitizedFileName}`;

    // Upload to temporary location first
    const tempPath = `temp/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from('company-documents')
      .upload(tempPath, file);

    if (uploadError) throw uploadError;

    // Save to driver folder in 'Extra Uploads' subfolder
    const result = await saveExtraUploadToDriverFolder({
      originalFilePath: tempPath,
      originalFileName: file.name,
      driverName: driverName,
      pickupDate: pickupDate,
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to save file to driver folder');
    }

    // Create load_documents record
    if (dispatch.loadId && result.newFilePath) {
      console.log('📄 Looking for document with file_path:', result.newFilePath);
      
      // Get the document ID from the documents table
      const { data: docData, error: docError } = await supabase
        .from('documents')
        .select('id')
        .eq('file_path', result.newFilePath)
        .maybeSingle();

      console.log('📄 Document lookup result:', docData, 'Error:', docError);

      if (docData) {
        const { error: insertError } = await supabase
          .from('load_documents')
          .insert({
            load_id: dispatch.loadId,
            document_id: docData.id,
            document_type: 'other'
          });
        
        if (insertError) {
          console.error('Error creating load_documents record:', insertError);
        } else {
          console.log('✅ Created load_documents record for document:', docData.id);
        }
      } else {
        console.warn('⚠️ Document not found in documents table for path:', result.newFilePath);
      }
    }

    // Clean up temp file
    await supabase.storage
      .from('company-documents')
      .remove([tempPath]);
  }

  async function syncDocumentsToDocumentsPage(docs: any[]) {
    try {
      // Get or create "Dispatches" folder
      let { data: dispatchesFolder } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', 'Dispatches')
        .is('parent_folder_id', null)
        .single();

      if (!dispatchesFolder) {
        const { data: newFolder } = await supabase
          .from('document_folders')
          .insert({ name: 'Dispatches', is_system_folder: true })
          .select()
          .single();
        dispatchesFolder = newFolder;
      }

      if (!dispatchesFolder) return;

      // Get or create dispatch-specific subfolder
      let { data: dispatchFolder } = await supabase
        .from('document_folders')
        .select('id')
        .eq('dispatch_id', dispatchId)
        .eq('parent_folder_id', dispatchesFolder.id)
        .single();

      if (!dispatchFolder) {
        const { data: newDispatchFolder } = await supabase
          .from('document_folders')
          .insert({
            name: dispatch?.dispatchNumber || `Dispatch-${dispatchId.slice(0, 8)}`,
            parent_folder_id: dispatchesFolder.id,
            dispatch_id: dispatchId,
            is_system_folder: false
          })
          .select()
          .single();
        dispatchFolder = newDispatchFolder;
      }

      if (!dispatchFolder) return;

      // Add documents to the folder (as references, not actual uploads)
      // Get existing documents count for numbering
      const { data: existingDocs } = await supabase
        .from('documents')
        .select('id, name')
        .eq('folder_id', dispatchFolder.id);
      
      let docCounter = (existingDocs?.length || 0) + 1;
      
      for (const doc of docs) {
        // Check if document already exists by URL (prevent duplicates)
        const { data: existing } = await supabase
          .from('documents')
          .select('id')
          .eq('file_path', doc.url)
          .eq('folder_id', dispatchFolder.id)
          .maybeSingle();

        if (!existing) {
          // Simple filename: DriverName_DispatchID or DriverName_DispatchID_2
          const extension = doc.filename ? doc.filename.split('.').pop() : 'file';
          const driverName = dispatch?.driverName?.replace(/\s+/g, '_') || 'Driver';
          const baseDocName = `${driverName}_${dispatch?.dispatchNumber}`;
          
          // If this is the first doc, no number. Otherwise add counter
          const docName = docCounter === 1 
            ? `${baseDocName}.${extension}`
            : `${baseDocName}_${docCounter}.${extension}`;
          
          await supabase
            .from('documents')
            .insert({
              name: docName,
              file_path: doc.url,
              file_size: doc.size || 0,
              file_type: doc.type || 'unknown',
              mime_type: doc.contentType,
              folder_id: dispatchFolder.id
            });
          
          docCounter++;
        }
      }
    } catch (error) {
      console.error('Error syncing documents:', error);
    }
  }

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      assigned: 'bg-blue-100 text-blue-700 border-blue-300',
      'in-transit': 'bg-green-100 text-green-700 border-green-300',
      delivered: 'bg-slate-100 text-slate-700 border-slate-300',
      cancelled: 'bg-red-100 text-red-700 border-red-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dispatch) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Dispatch Not Found</h2>
        <p className="text-slate-600 mb-4">The dispatch you're looking for doesn't exist.</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Back to Dispatches
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{dispatch.dispatchNumber}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(dispatch.status)}`}>
                  {dispatch.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-600">
                <span>Created on {new Date(dispatch.assignedAt).toLocaleString()}</span>
                <span>by {dispatch.assignedBy}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => {
                const newPath = `dispatches/edit/${dispatch.id}`;
                window.history.pushState({}, '', `/${newPath}`);
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Dispatch
            </button>
            <button className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2">
              <History className="w-4 h-4" />
              Change Log
            </button>
            {dispatch.status === 'assigned' && (
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                <Play className="w-4 h-4" />
                Activate Dispatch
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-6">
            {[
              { id: 'summary', label: 'Summary' },
              { id: 'documents', label: 'Documents' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-3 px-1 font-medium transition-colors border-b-2 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Load Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Load Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Load Details
                </h2>
                <span className="text-blue-600 font-semibold">{dispatch.loadNumber}</span>
              </div>

              {/* Pickup */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-600 mb-1">Pickup</div>
                    <div className="font-semibold text-slate-900">{dispatch.pickupLocation || 'N/A'}</div>
                    <div className="text-sm text-slate-600">{dispatch.pickupCity}, {dispatch.pickupState}</div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(dispatch.pickupTime).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock className="w-4 h-4" />
                        {new Date(dispatch.pickupTime).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-600 mb-1">Delivery</div>
                    <div className="font-semibold text-slate-900">{dispatch.deliveryLocation || 'N/A'}</div>
                    <div className="text-sm text-slate-600">{dispatch.deliveryCity}, {dispatch.deliveryState}</div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(dispatch.deliveryTime).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Clock className="w-4 h-4" />
                        {new Date(dispatch.deliveryTime).toLocaleTimeString()}
                      </div>
                    </div>
                    
                    {/* Rescheduled Delivery Info */}
                    {dispatch.rescheduledDeliveryDate && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-orange-600 font-semibold">⚠️ Rescheduled Delivery</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1 text-orange-600">
                            <Calendar className="w-4 h-4" />
                            {(() => {
                              const dateStr = dispatch.rescheduledDeliveryDate;
                              const timeStr = dispatch.rescheduledDeliveryTimeFrom || '00:00:00';
                              const combinedDateTime = `${dateStr}T${timeStr}`;
                              return new Date(combinedDateTime).toLocaleString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              });
                            })()}
                          </div>
                          {dispatch.rescheduledDeliveryTimeFrom && dispatch.rescheduledDeliveryTimeTo && (
                            <div className="flex items-center gap-1 text-orange-600">
                              <Clock className="w-4 h-4" />
                              Window: {dispatch.rescheduledDeliveryTimeFrom} - {dispatch.rescheduledDeliveryTimeTo}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* All Stops Section */}
              {dispatch.allStops && dispatch.allStops.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    {dispatch.allStops.length > 2 ? `All Stops (${dispatch.allStops.length})` : 'Stop Details'}
                  </h3>
                  <div className="space-y-3">
                    {dispatch.allStops.map((stop: any, index: number) => {
                      // Calculate display number: PICKUP is always #1, deliveries are numbered sequentially
                      const deliveryStops = dispatch.allStops.filter((s: any) => s.stop_type === 'consignee');
                      const deliveryIndex = deliveryStops.findIndex((s: any) => s.stop_order === stop.stop_order);
                      const displayNumber = stop.stop_type === 'shipper' ? 1 : deliveryIndex + 1;
                      
                      return (
                        <div 
                          key={index}
                          className={`p-4 rounded-lg border-2 ${
                            stop.stop_type === 'shipper' 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${
                                  stop.stop_type === 'shipper' 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-blue-600 text-white'
                                }`}>
                                  {stop.stop_type === 'shipper' ? '📦 PICKUP' : '🎯 DELIVERY'} #{displayNumber}
                                </span>
                                {stop.facility_name && (
                                  <span className="font-semibold text-slate-900">{stop.facility_name}</span>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div>
                                <p className="text-xs text-slate-600 mb-1">Address</p>
                                <p className="font-medium text-slate-900">
                                  {stop.street && `${stop.street}, `}
                                  {stop.city}, {stop.state} {stop.zip_code}
                                </p>
                              </div>
                              
                              <div>
                                <p className="text-xs text-slate-600 mb-1">Date & Time</p>
                                <p className="font-medium text-slate-900">
                                  {new Date(stop.date).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                  {stop.time_from && ` at ${stop.time_from}`}
                                  {stop.time_to && ` - ${stop.time_to}`}
                                </p>
                              </div>
                              
                              <div>
                                {stop.contact_person && (
                                  <div className="mb-2">
                                    <p className="text-xs text-slate-600">Contact</p>
                                    <p className="font-medium text-slate-900">{stop.contact_person}</p>
                                  </div>
                                )}
                                {stop.phone_number && (
                                  <div>
                                    <p className="text-xs text-slate-600">Phone</p>
                                    <p className="font-medium text-slate-900">{stop.phone_number}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {(stop.weight || stop.pieces) && (
                              <div className="mt-3 pt-3 border-t border-slate-200 flex gap-4">
                                {stop.weight && (
                                  <div>
                                    <p className="text-xs text-slate-600">Weight</p>
                                    <p className="font-semibold text-slate-900">{stop.weight.toLocaleString()} lbs</p>
                                  </div>
                                )}
                                {stop.pieces && (
                                  <div>
                                    <p className="text-xs text-slate-600">Pieces</p>
                                    <p className="font-semibold text-slate-900">{stop.pieces}</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {stop.instructions && (
                              <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-xs text-slate-600 mb-1">Instructions</p>
                                <p className="text-sm text-slate-700">{stop.instructions}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Rate Information */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-slate-600 mb-1">Total Rate</div>
                    <div className="text-2xl font-bold text-green-600">${dispatch.rate.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 mb-1">Distance</div>
                    <div className="text-2xl font-bold text-blue-600">{dispatch.distance} miles</div>
                  </div>
                  <div>
                    <div className="text-sm text-slate-600 mb-1">Rate per Mile</div>
                    <div className="text-lg font-semibold text-slate-900">
                      ${(dispatch.rate / dispatch.distance).toFixed(2)}/mi
                    </div>
                  </div>
                  {dispatch.driverType !== 'company_driver' && (
                    <>
                      <div>
                        <div className="text-sm text-slate-600 mb-1">Driver Pay</div>
                        <div className="text-2xl font-bold text-purple-600">${dispatch.driverPay.toLocaleString()}</div>
                        <div className="text-xs text-slate-500">
                          {dispatch.rate > 0 ? `${((dispatch.driverPay / dispatch.rate) * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-slate-600 mb-1">Company Cut</div>
                        <div className="text-2xl font-bold text-orange-600">${(dispatch.rate - dispatch.driverPay).toLocaleString()}</div>
                        <div className="text-xs text-slate-500">
                          {dispatch.rate > 0 ? `${(((dispatch.rate - dispatch.driverPay) / dispatch.rate) * 100).toFixed(1)}%` : '0%'}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Special Instructions */}
            {dispatch.specialInstructions && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Special Instructions
                </h2>
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {dispatch.specialInstructions.split('|').map((instruction: string, index: number) => (
                      <span key={index}>
                        {instruction.trim()}
                        {index < dispatch.specialInstructions!.split('|').length - 1 && (
                          <>
                            <br />
                            <br />
                          </>
                        )}
                      </span>
                    ))}
                  </p>
                </div>
              </div>
            )}

            {/* Notes */}
            {dispatch.notes && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Notes
                </h2>
                <p className="text-slate-700 whitespace-pre-wrap">{dispatch.notes}</p>
              </div>
            )}
          </div>

          {/* Driver & Equipment Sidebar */}
          <div className="space-y-6">
            {/* Driver Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Driver
                </h2>
                <button className="p-1 hover:bg-slate-100 rounded">
                  <Edit className="w-4 h-4 text-slate-600" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-slate-600">Name</div>
                  <div className="font-semibold text-slate-900">{dispatch.driverName}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">Phone</div>
                  <div className="font-semibold text-slate-900">{dispatch.driverPhone || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">Driver ID</div>
                  <div className="font-semibold text-blue-600">{formatDriverId(dispatch.driverId)}</div>
                </div>
              </div>
            </div>

            {/* Truck Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Truck
                </h2>
                <button className="p-1 hover:bg-slate-100 rounded">
                  <Edit className="w-4 h-4 text-slate-600" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-slate-600">Unit Number</div>
                  <div className="font-semibold text-slate-900">{dispatch.truckNumber}</div>
                </div>
                <div>
                  <div className="text-sm text-slate-600">Truck ID</div>
                  <div className="font-semibold text-blue-600">{formatTruckId(dispatch.truckId)}</div>
                </div>
              </div>
            </div>

            {/* Route Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Route Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Loaded Miles</span>
                  <span className="font-semibold text-slate-900">{dispatch.distance}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Empty Miles</span>
                  <span className="font-semibold text-slate-900">{dispatch.emptyMiles || 0}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-slate-200">
                  <span className="text-slate-600 font-semibold">Total Miles</span>
                  <span className="font-bold text-slate-900">{dispatch.distance + (dispatch.emptyMiles || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS History Tab - Removed per user request */}
      {false && (
        <div className="space-y-6">
          {/* SMS Status Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                SMS Notification Status
              </h2>
              <button
                onClick={handleResendSMS}
                disabled={resendingSMS}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${resendingSMS ? 'animate-spin' : ''}`} />
                {resendingSMS ? 'Sending...' : 'Resend SMS'}
              </button>
            </div>
            {smsStatus ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {smsStatus.status === 'sent' || smsStatus.status === 'delivered' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : smsStatus.status === 'failed' ? (
                    <XCircle className="w-5 h-5 text-red-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">
                      {smsStatus.status === 'sent' ? 'SMS Sent Successfully' :
                       smsStatus.status === 'delivered' ? 'SMS Delivered' :
                       smsStatus.status === 'failed' ? 'SMS Failed' :
                       'SMS Pending'}
                    </p>
                    {smsStatus.error && (
                      <p className="text-sm text-red-600 mt-1">{smsStatus.error}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-slate-600">No SMS notification sent for this dispatch</p>
            )}
          </div>

          {/* SMS Logs - iPhone Style */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">SMS History</h2>
            <div className="mx-auto max-w-4xl">
              {smsLogs.length > 0 ? (
                <div className="bg-slate-50 rounded-lg p-4 max-h-[500px] overflow-y-auto mb-4">
                  <div className="space-y-3 px-8">
                    {smsLogs.map((log, index) => (
                      <div key={log.id || index} className={`flex ${log.direction === 'outbound' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[70%] ${log.direction === 'outbound' ? '' : 'flex flex-col items-end'}`}>
                          <div className={`rounded-2xl px-4 py-2 shadow-sm ${
                            log.direction === 'outbound' 
                              ? 'bg-slate-200 text-slate-900' 
                              : 'bg-blue-500 text-white'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap break-words">{log.content || log.message_text || log.message}</p>
                            
                            {/* Attachments */}
                            {log.attachments && log.attachments.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {log.attachments.map((att: any, idx: number) => (
                                  <div key={idx}>
                                    {att.type === 'image' || att.contentType?.startsWith('image/') ? (
                                      <button 
                                        onClick={() => setPreviewImage(att.url)}
                                        className="block cursor-pointer"
                                      >
                                        <img 
                                          src={att.url} 
                                          alt={att.filename || 'Image'}
                                          className="max-w-full rounded-lg border border-slate-300 hover:opacity-90 transition-opacity"
                                          style={{ maxHeight: '300px' }}
                                        />
                                      </button>
                                    ) : (
                                      <a 
                                        href={att.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                                          log.direction === 'inbound'
                                            ? 'bg-blue-600 border-blue-400 hover:bg-blue-700 text-white'
                                            : 'bg-white border-slate-300 hover:bg-slate-50 text-slate-900'
                                        }`}
                                      >
                                        {att.contentType?.includes('pdf') ? (
                                          <FileText className="w-4 h-4" />
                                        ) : (
                                          <Download className="w-4 h-4" />
                                        )}
                                        <span className="text-xs font-medium truncate">
                                          {att.filename || 'Download'}
                                        </span>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1 px-2">
                            {(log.sent_at || log.received_at || log.created_at) ? new Date(log.sent_at || log.received_at || log.created_at).toLocaleTimeString('en-US', { 
                              hour: 'numeric', 
                              minute: '2-digit',
                              hour12: true 
                            }) : 'Pending'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-8 mb-4">
                  <p className="text-slate-600 text-center">No SMS messages sent yet</p>
                </div>
              )}
              
              {/* Message Input */}
              <div className="bg-white border border-slate-200 rounded-lg p-3 flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type a message to the driver..."
                  className="flex-1 px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                >
                  {sendingMessage ? 'Sending...' : 'Send'}
                  <MessageSquare className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  {documents.length} {documents.length === 1 ? 'document' : 'documents'}
                </span>
                {/* View Toggle */}
                <div className="flex items-center bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => setDocumentsViewMode('grid')}
                    className={`p-1.5 rounded transition-colors ${
                      documentsViewMode === 'grid'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    title="Grid View"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDocumentsViewMode('list')}
                    className={`p-1.5 rounded transition-colors ${
                      documentsViewMode === 'list'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                    title="List View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                {uploadProgress && uploadProgress.current > 0 && (
                  <span className="text-xs text-slate-600 truncate max-w-[220px]">
                    Uploading {uploadProgress.current}/{uploadProgress.total}: {uploadProgress.label}
                  </span>
                )}
                <label className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2 ${
                  uploadProgress ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" x2="12" y1="3" y2="15"></line>
                  </svg>
                  Upload File
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    disabled={uploadProgress !== null}
                  />
                </label>
              </div>
            </div>

            {documents.length > 0 ? (
              documentsViewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc, idx) => (
                  <div 
                    key={`${doc.messageId}-${idx}`}
                    className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all"
                  >
                    {/* Document Preview */}
                    <div className="mb-3">
                      <DocumentPreview 
                        doc={doc} 
                        onImageClick={() => setPreviewImage(doc.url)}
                        onPdfClick={(url) => setPreviewPdf(url)}
                      />
                    </div>

                    {/* Document Info */}
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-slate-900 truncate flex-1">
                          {doc.source === 'load_documents' ? (
                            doc.name || 'Document'
                          ) : (
                            (() => {
                              const extension = doc.filename ? doc.filename.split('.').pop() : 'file';
                              const driverName = dispatch?.driverName?.replace(/\s+/g, '_') || 'Driver';
                              const docNum = idx + 1;
                              return docNum === 1 
                                ? `${driverName}_${dispatch?.dispatchNumber}.${extension}`
                                : `${driverName}_${dispatch?.dispatchNumber}_${docNum}.${extension}`;
                            })()
                          )}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          doc.source === 'load_documents'
                            ? 'bg-purple-100 text-purple-700'
                            : doc.direction === 'inbound' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                        }`}>
                          {doc.source === 'load_documents' 
                            ? (doc.document_type === 'rate_confirmation' ? 'Rate Conf' : doc.document_type?.replace('_', ' ') || 'Document')
                            : doc.direction === 'inbound' ? 'Received' : 'Sent'}
                        </span>
                      </div>

                      {doc.messageContent && (
                        <p className="text-xs text-slate-600 line-clamp-2">
                          {doc.messageContent}
                        </p>
                      )}

                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>
                          {new Date(doc.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        {doc.contentType && (
                          <span className="uppercase">{doc.contentType.split('/')[1]}</span>
                        )}
                      </div>

                      {/* Download Button - saves the file, never opens it */}
                      <button
                        onClick={() => handleDownloadDocument(doc, idx)}
                        disabled={downloadingDocKey === `${doc.messageId}-${idx}`}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {downloadingDocKey === `${doc.messageId}-${idx}` ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc, idx) => {
                  const extension = doc.filename ? doc.filename.split('.').pop() : 'file';
                  const driverName = dispatch?.driverName?.replace(/\s+/g, '_') || 'Driver';
                  const docNum = idx + 1;
                  const fileName = doc.source === 'load_documents' 
                    ? (doc.name || 'Document')
                    : docNum === 1 
                      ? `${driverName}_${dispatch?.dispatchNumber}.${extension}`
                      : `${driverName}_${dispatch?.dispatchNumber}_${docNum}.${extension}`;
                  
                  const getFileIcon = () => {
                    if (doc.contentType?.includes('pdf') || doc.type?.includes('pdf')) return '📄';
                    if (doc.contentType?.includes('image') || doc.type?.includes('image')) return '🖼️';
                    if (doc.contentType?.includes('word') || doc.contentType?.includes('document')) return '📝';
                    return '📎';
                  };

                  return (
                    <div 
                      key={`${doc.messageId}-${idx}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="text-2xl">{getFileIcon()}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{fileName}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>
                              {new Date(doc.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              doc.source === 'load_documents'
                                ? 'bg-purple-100 text-purple-700'
                                : doc.direction === 'inbound' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-blue-100 text-blue-700'
                            }`}>
                              {doc.source === 'load_documents' 
                                ? (doc.document_type === 'rate_confirmation' ? 'Rate Conf' : doc.document_type?.replace('_', ' ') || 'Document')
                                : doc.direction === 'inbound' ? 'Received' : 'Sent'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDownloadDocument(doc, idx)}
                        disabled={downloadingDocKey === `${doc.messageId}-${idx}`}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {downloadingDocKey === `${doc.messageId}-${idx}` ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            Download
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Documents Yet</h3>
                <p className="text-slate-600">
                  Rate confirmations and other load documents will appear here automatically
                </p>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-7xl max-h-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img 
              src={previewImage} 
              alt="Preview" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewPdf && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewPdf(null)}
        >
          <div className="relative w-full max-w-6xl h-[90vh] bg-white rounded-lg">
            <button
              onClick={() => setPreviewPdf(null)}
              className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors z-10"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src={previewPdf}
              className="w-full h-full rounded-lg"
              title="PDF Preview"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}
